//@flow
import React, { useMemo } from "react";
import { getDisplayName } from "data/constants";
import { type MultiSelectionProps, type PaginationProps } from "data/types";
import { DEFAULT_MULTISELECTION, DEFAULT_TRACK_CURRENT_ROW } from "./utils";
export { useDocListPagination } from "./useDocListPagination";
export { useSearchPagination } from "./useSearchPagination";

type Config = {
    namespace: string,
    usePagination: (props: any) => PaginationProps,
};

export type PaginationHandlerProps = {
    /* set current index */
    setCurrent: (rowIndex: number) => number,
    /* get itemUri by index */
    getActiveRowItemUri: (index: number) => string,
    /* callback to update when pages are loaded */
    onRowsUpdate: (result: any) => void,
    /* callback (search results only) to handle pages loaded */
    onRowsLoaded?: (rows: any, totalCount: number) => void,
};

export const withPaginationHandler =
    ({ namespace, usePagination }: Config) =>
    (WrappedComponent: ComponentType<any>) => {
        type Props = {
            multiSelection?: MultiSelectionProps,
            [namespace]: TrackCurrentRowProps,
        };
        const HOC = (props: Props) => {
            const {
                multiSelection: {
                    multiple,
                    activeRows,
                } = DEFAULT_MULTISELECTION,
                history,
                isMini,
            } = props;

            const { currentRow, setCurrentRow } =
                props[namespace] || DEFAULT_TRACK_CURRENT_ROW;

            const {
                getProps,
                setCurrent,
                handleRowsUpdate,
                handleRowsLoaded,
            }: PaginationProps = usePagination({
                history,
                setCurrentRow,
                multiple,
                activeRows,
                isMini,
                currentRow,
            });

            const pagination: PaginationHandlerProps = useMemo(
                () => ({
                    getProps,
                    setCurrent,
                    onRowsUpdate: handleRowsUpdate,
                    onRowsLoaded: handleRowsLoaded,
                }),
                [getProps, setCurrent, handleRowsUpdate, handleRowsLoaded]
            );

            return <WrappedComponent {...props} pagination={pagination} />;
        };
        HOC.displayName = `withPaginationHandler(${getDisplayName(
            WrappedComponent
        )})`;
        return HOC;
    };

export default withPaginationHandler;
